import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ALL_SUBJECTS, DAYS } from "@/lib/constants";
import { Loader2, Plus, Trash2, Send, LayoutTemplate, X, Pencil, Save } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const DAY_SHORT = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];
const EMPTY_CELL = { unit: "", pages: "", questions: "", correct: "", wrong: "", blank: "" };
const newRow = () => ({ subject: "", resource_name: "", days: Object.fromEntries(DAYS.map((d) => [d, { ...EMPTY_CELL }])) });
const newWeek = (n) => ({ week: n, rows: [newRow()] });
const fillRow = (r) => ({
  subject: r.subject || "", resource_name: r.resource_name || "",
  days: Object.fromEntries(DAYS.map((d) => {
    const c = (r.days || {})[d] || {};
    return [d, { unit: c.unit || "", pages: c.pages || "", questions: c.questions || "", correct: c.correct || "", wrong: c.wrong || "", blank: c.blank || "" }];
  })),
});

export default function ProgramTemplates() {
  const [items, setItems] = useState([]);
  const [students, setStudents] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [editId, setEditId] = useState(null);
  const [name, setName] = useState("");
  const [publisher, setPublisher] = useState("");
  const [level, setLevel] = useState("TYT");
  const [weeks, setWeeks] = useState([newWeek(1)]);
  const [aw, setAw] = useState(0);
  const [busy, setBusy] = useState(false);
  const [assignId, setAssignId] = useState(null);
  const [targetType, setTargetType] = useState("student");
  const [sid, setSid] = useState("");
  const [groupIds, setGroupIds] = useState([]);
  const [tracksSel, setTracksSel] = useState([]);
  const [startWeek, setStartWeek] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, st, m] = await Promise.all([api.get("/program-templates"), api.get("/students"), api.get("/meta")]);
      setItems(t.data);
      setStudents(st.data);
      setTracks(m.data.tracks || []);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setRow = (wi, ri, k, v) =>
    setWeeks((ws) => ws.map((w, i) => (i === wi ? { ...w, rows: w.rows.map((r, j) => (j === ri ? { ...r, [k]: v } : r)) } : w)));
  const setCell = (wi, ri, day, k, v) =>
    setWeeks((ws) =>
      ws.map((w, i) =>
        i === wi
          ? { ...w, rows: w.rows.map((r, j) => (j === ri ? { ...r, days: { ...r.days, [day]: { ...r.days[day], [k]: v } } } : r)) }
          : w
      )
    );

  const resetForm = () => {
    setEditId(null);
    setName("");
    setPublisher("");
    setLevel("TYT");
    setWeeks([newWeek(1)]);
    setAw(0);
  };

  const openNew = () => { resetForm(); setModal(true); };

  const openEdit = async (id) => {
    try {
      const { data } = await api.get(`/program-templates/${id}`);
      setEditId(id);
      setName(data.name || "");
      setPublisher(data.publisher || "");
      setLevel(data.level || "TYT");
      const ws = (data.weeks || []).map((w, i) => ({ week: w.week || i + 1, rows: (w.rows || []).length ? w.rows.map(fillRow) : [newRow()] }));
      setWeeks(ws.length ? ws : [newWeek(1)]);
      setAw(0);
      setModal(true);
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const save = async () => {
    if (!name.trim()) return toast.error("Program adı zorunlu");
    setBusy(true);
    try {
      const body = { name: name.trim(), publisher: publisher.trim(), level, weeks };
      if (editId) {
        const { data } = await api.put(`/program-templates/${editId}`, body);
        toast.success(data.synced_students ? `Program güncellendi — ${data.synced_students} öğrencinin haftalık programı otomatik yenilendi` : "Program güncellendi");
      } else {
        await api.post("/program-templates", body);
        toast.success("Program şablonu kaydedildi");
      }
      setModal(false);
      resetForm();
      load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const del = async (t) => {
    if (!window.confirm(`"${t.name}" programı silinecek ve bu programdan öğrencilere atanmış tüm haftalık program dersleri otomatik kaldırılacak. Emin misiniz?`)) return;
    try {
      const { data } = await api.delete(`/program-templates/${t.id}`);
      toast.success(`Program silindi${data.removed ? ` — öğrenci programlarından ${data.removed} ders kaldırıldı` : ""}`);
      load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const toggleGroup = (id) => setGroupIds((g) => (g.includes(id) ? g.filter((x) => x !== id) : [...g, id]));
  const toggleTrack = (t) =>
    setTracksSel((prev) => {
      if (prev.includes(t)) return prev.filter((x) => x !== t);
      if (prev.length >= 3) { toast.error("En fazla 3 sınıf/alan seçebilirsiniz"); return prev; }
      return [...prev, t];
    });

  const assign = async () => {
    if (targetType === "student" && !sid) return toast.error("Öğrenci seçin");
    if (targetType === "group" && groupIds.length === 0) return toast.error("Öğrenci seçin");
    if (targetType === "track" && tracksSel.length === 0) return toast.error("Sınıf/alan seçin");
    setBusy(true);
    try {
      const body = { target: targetType, start_week: Number(startWeek) };
      if (targetType === "student") body.student_id = sid;
      if (targetType === "group") body.student_ids = groupIds;
      if (targetType === "track") body.tracks = tracksSel;
      const { data } = await api.post(`/program-templates/${assignId}/assign`, body);
      toast.success(`${data.students} öğrenciye ${data.created} ders atandı — Haftalık Program'da görünür`);
      setAssignId(null);
      setSid("");
      setGroupIds([]);
      setTracksSel([]);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const week = weeks[aw];

  return (
    <div className="space-y-5" data-testid="program-templates">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-xs font-semibold text-slate-400 flex items-center gap-1.5"><LayoutTemplate size={13} /> Çok Haftalı Kaynak Programı</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900 mt-1">Program Şablonları</h1>
          <p className="text-slate-500 text-sm mt-1">{isAdmin ? "Bir kaynağın kaç haftada biteceğini hafta hafta planla (ders × gün, ünite + sayfa aralığı), öğrenciye/gruba/sınıfa ata. Düzenleme ve silme öğrenci ekranlarına otomatik yansır." : "Hazır program şablonlarını kendi öğrencilerine, grubuna veya sınıfına ata."}</p>
        </div>
        {isAdmin && (
        <Button onClick={openNew} className="bg-slate-900 hover:bg-slate-800" data-testid="tpl-new-btn">
          <Plus size={16} /> <span className="ml-1">Yeni Program</span>
        </Button>
        )}
      </div>

      {loading ? (
        <div className="py-12 text-center text-slate-500"><Loader2 className="animate-spin inline" size={22} /></div>
      ) : items.length === 0 ? (
        <div className="card-ta p-12 text-center text-slate-500">Henüz program şablonu yok — "Yeni Program" ile başlayın.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="tpl-list">
          {items.map((t) => (
            <div key={t.id} className="card-ta p-4" data-testid={`tpl-card-${t.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-display font-bold text-slate-900 truncate">{t.name}</div>
                  <div className="text-xs text-slate-500 mt-1">{t.publisher && <span className="font-bold text-indigo-700 uppercase">{t.publisher} · </span>}{t.weeks} hafta · {t.rows} ders satırı</div>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full border font-semibold shrink-0 ${t.level === "TYT" ? "bg-blue-100 text-blue-700 border-blue-200" : "bg-amber-100 text-amber-700 border-amber-200"}`}>{t.level}</span>
              </div>
              <div className="flex items-center gap-2 mt-3">
                <Button size="sm" onClick={() => setAssignId(assignId === t.id ? null : t.id)} className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700" data-testid={`tpl-assign-${t.id}`}>
                  <Send size={12} /> <span className="ml-1">Ata</span>
                </Button>
                {isAdmin && (<>
                <Button size="sm" variant="outline" onClick={() => openEdit(t.id)} className="h-8 text-xs border-indigo-300 text-indigo-700 hover:bg-indigo-50 ml-auto" data-testid={`tpl-edit-${t.id}`}>
                  <Pencil size={12} /> <span className="ml-1">Düzelt</span>
                </Button>
                <Button size="sm" variant="outline" onClick={() => del(t)} className="h-8 text-xs border-rose-300 text-rose-600 hover:bg-rose-50" data-testid={`tpl-del-${t.id}`}>
                  <Trash2 size={12} /> <span className="ml-1">Sil</span>
                </Button>
                </>)}
              </div>

              {assignId === t.id && (
                <div className="mt-3 rounded-xl border border-slate-200 p-3 space-y-3 bg-slate-50" data-testid={`tpl-assign-box-${t.id}`}>
                  <div className="flex rounded-lg border border-slate-200 overflow-hidden bg-white">
                    {[["student", "Tek Öğrenci"], ["group", "Grup"], ["track", "Sınıf / Alan"]].map(([id, label]) => (
                      <button key={id} onClick={() => setTargetType(id)} data-testid={`tpl-target-${id}`}
                        className={`flex-1 px-2 py-1.5 text-xs font-semibold transition-colors ${targetType === id ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-100"}`}>{label}</button>
                    ))}
                  </div>
                  {targetType === "student" && (
                    <select className="ta-input" value={sid} onChange={(e) => setSid(e.target.value)} data-testid="tpl-assign-student">
                      <option value="">Öğrenci seç</option>
                      {students.map((s) => <option key={s.id} value={s.id}>{s.name}{s.track ? ` · ${s.track}` : ""}</option>)}
                    </select>
                  )}
                  {targetType === "group" && (
                    <div className="max-h-36 overflow-y-auto space-y-1 bg-white rounded-lg border border-slate-200 p-2" data-testid="tpl-assign-group">
                      {students.map((s) => (
                        <label key={s.id} className="flex items-center gap-2 text-xs px-1 py-1 rounded hover:bg-slate-50 cursor-pointer" data-testid={`tpl-group-${s.id}`}>
                          <input type="checkbox" checked={groupIds.includes(s.id)} onChange={() => toggleGroup(s.id)} />
                          <span className="truncate">{s.name}</span>
                        </label>
                      ))}
                    </div>
                  )}
                  {targetType === "track" && (
                    <div className="flex flex-wrap gap-1.5" data-testid="tpl-assign-tracks">
                      {tracks.map((tr) => (
                        <button key={tr} onClick={() => toggleTrack(tr)} data-testid={`tpl-track-${tr}`}
                          className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${tracksSel.includes(tr) ? "bg-indigo-900 text-white border-indigo-900" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-100"}`}>{tr}</button>
                      ))}
                      <div className="w-full text-[11px] text-slate-500">En fazla 3 sınıf/alan seçilebilir.</div>
                    </div>
                  )}
                  <div className="flex items-end gap-2">
                    <div>
                      <label className="ta-label">Başlangıç Haftası</label>
                      <input type="number" min="1" className="ta-input w-24" value={startWeek} onChange={(e) => setStartWeek(e.target.value)} data-testid="tpl-assign-week" />
                    </div>
                    <Button onClick={assign} disabled={busy} className="bg-indigo-900 hover:bg-indigo-800 h-9" data-testid="tpl-assign-submit">
                      {busy ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />} <span className="ml-1">Ata</span>
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="tpl-modal">
          <div className="absolute inset-0 bg-slate-900/50" onClick={() => setModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[92vh] overflow-y-auto p-6">
            <button onClick={() => setModal(false)} className="absolute top-4 right-4 text-slate-400 hover:text-slate-700 z-10" data-testid="tpl-modal-close"><X size={20} /></button>
            <h2 className="font-display text-xl font-extrabold text-slate-900">{editId ? "Programı Düzenle" : "Yeni Çalışma Programı"}</h2>
            <p className="text-sm text-slate-500 mt-0.5 mb-4">{editId ? "Değişiklikler bu programın atandığı tüm öğrencilerin haftalık programına otomatik yansır." : "Kaynak başlığı gir, hafta hafta ders satırları ile ünite/sayfa/soru doldur."}</p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
              <div>
                <label className="ta-label">Kaynak / Program Adı</label>
                <input className="ta-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Örn: HIZ YAYINLARI TYT SETİ" data-testid="tpl-name" />
              </div>
              <div>
                <label className="ta-label">Yayınevi</label>
                <input className="ta-input" value={publisher} onChange={(e) => setPublisher(e.target.value)} placeholder="Örn: Hız Yayınları" data-testid="tpl-publisher" />
              </div>
              <div>
                <label className="ta-label">Tür</label>
                <select className="ta-input" value={level} onChange={(e) => setLevel(e.target.value)} data-testid="tpl-level">
                  <option value="TYT">TYT</option>
                  <option value="AYT">AYT</option>
                </select>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mb-4">
              {weeks.map((w, i) => (
                <div key={i} className={`flex items-center rounded-lg text-sm font-semibold transition-colors ${aw === i ? "bg-indigo-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                  <button onClick={() => setAw(i)} data-testid={`tpl-week-${i}`} className="px-3.5 py-1.5">{w.week}. Hafta</button>
                  {weeks.length > 1 && (
                    <button onClick={() => { setWeeks((ws) => ws.filter((_, j) => j !== i).map((x, j) => ({ ...x, week: j + 1 }))); setAw((a) => Math.max(0, Math.min(a, weeks.length - 2))); }}
                      className="pr-2 opacity-60 hover:opacity-100" title="Haftayı sil" data-testid={`tpl-week-del-${i}`}><X size={13} /></button>
                  )}
                </div>
              ))}
              <button onClick={() => { setWeeks((ws) => [...ws, newWeek(ws.length + 1)]); setAw(weeks.length); }}
                className="px-3.5 py-1.5 rounded-lg text-sm font-semibold border border-slate-300 text-slate-600 hover:bg-slate-50 flex items-center gap-1" data-testid="tpl-week-add">
                <Plus size={14} /> Hafta Ekle
              </button>
            </div>

            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-bold text-slate-700">{week.week}. Hafta — Ders Satırları × 7 Gün</span>
              <Button variant="outline" size="sm" onClick={() => setWeeks((ws) => ws.map((w, i) => (i === aw ? { ...w, rows: [...w.rows, newRow()] } : w)))} className="border-slate-300 h-8 text-xs" data-testid="tpl-row-add">
                <Plus size={13} /> <span className="ml-1">Ders Satırı Ekle</span>
              </Button>
            </div>

            <div className="space-y-3">
              {week.rows.map((r, ri) => (
                <div key={ri} className="rounded-xl border border-slate-200 p-3" data-testid={`tpl-row-${ri}`}>
                  <div className="flex gap-2 mb-2">
                    <select className="ta-input w-44" value={r.subject} onChange={(e) => setRow(aw, ri, "subject", e.target.value)} data-testid={`tpl-subject-${ri}`}>
                      <option value="">Ders</option>
                      {ALL_SUBJECTS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <input className="ta-input flex-1" value={r.resource_name} onChange={(e) => setRow(aw, ri, "resource_name", e.target.value)} placeholder="Kaynak adı (opsiyonel)" data-testid={`tpl-resource-${ri}`} />
                    <button onClick={() => setWeeks((ws) => ws.map((w, i) => (i === aw ? { ...w, rows: w.rows.length > 1 ? w.rows.filter((_, j) => j !== ri) : [newRow()] } : w)))} className="text-rose-500 hover:text-rose-700" title="Satırı sil" data-testid={`tpl-row-del-${ri}`}><Trash2 size={16} /></button>
                  </div>
                  <div className="grid grid-cols-7 gap-1.5">
                    {DAYS.map((d, di) => (
                      <div key={d} className="space-y-1">
                        <div className="text-center text-[10px] font-bold text-slate-400 uppercase">{DAY_SHORT[di]}</div>
                        <input className="ta-input !px-1.5 !py-1 text-[11px]" value={r.days[d].unit} onChange={(e) => setCell(aw, ri, d, "unit", e.target.value)} placeholder="1. Ünite" data-testid={`tpl-cell-unit-${ri}-${di}`} />
                        <input className="ta-input !px-1.5 !py-1 text-[11px]" value={r.days[d].pages} onChange={(e) => setCell(aw, ri, d, "pages", e.target.value)} placeholder="8-20" data-testid={`tpl-cell-pages-${ri}-${di}`} />
                        <input className="ta-input !px-1.5 !py-1 text-[11px]" value={r.days[d].questions} onChange={(e) => setCell(aw, ri, d, "questions", e.target.value)} placeholder="soru" data-testid={`tpl-cell-q-${ri}-${di}`} />
                        <div className="grid grid-cols-3 gap-0.5">
                          <input className="ta-input !px-1 !py-0.5 text-[10px] !text-emerald-700" value={r.days[d].correct} onChange={(e) => setCell(aw, ri, d, "correct", e.target.value)} placeholder="D" title="Doğru" data-testid={`tpl-cell-c-${ri}-${di}`} />
                          <input className="ta-input !px-1 !py-0.5 text-[10px] !text-rose-600" value={r.days[d].wrong} onChange={(e) => setCell(aw, ri, d, "wrong", e.target.value)} placeholder="Y" title="Yanlış" data-testid={`tpl-cell-w-${ri}-${di}`} />
                          <input className="ta-input !px-1 !py-0.5 text-[10px] !text-slate-500" value={r.days[d].blank} onChange={(e) => setCell(aw, ri, d, "blank", e.target.value)} placeholder="B" title="Boş" data-testid={`tpl-cell-b-${ri}-${di}`} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <Button onClick={save} disabled={busy} className="w-full mt-5 bg-indigo-900 hover:bg-indigo-800 h-11" data-testid="tpl-save">
              {busy ? <Loader2 className="animate-spin" size={16} /> : editId ? <Save size={16} /> : <Plus size={16} />} <span className="ml-1">{editId ? "Değişiklikleri Kaydet" : "Programı Kaydet"}</span>
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
